import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Tag, Space, Select, Switch, Typography, Flex } from "antd";
import { ScheduleOutlined, PlusOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import InfoHint from "./common/InfoHint";
import DataTable from "./common/DataTable";
import AddVisit from "./Modals/addVisit";
import { listVisits, getUsers, bulkAssignVisits } from "../network/api";
import { useAuth } from "../context/AuthContext";
import { ROLES } from "../constants/roles";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import {
  VISIT_TYPE_LABEL,
  VISIT_TYPE_TAG_COLOR,
  VISIT_TYPE_OPTIONS,
  VISIT_STATUS_LABEL,
  VISIT_STATUS_TAG_COLOR,
  VISIT_STATUS_OPTIONS,
} from "../constants/visits";

const { Text } = Typography;
// Finished visits can't be (re)assigned.
const TERMINAL_STATUSES = ["completed", "cancelled"];

export default function Visits() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isHardwareAdmin, isSupervisor } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [status, setStatus] = useState(searchParams.get("status") || undefined);
  const [visitType, setVisitType] = useState();
  const [mine, setMine] = useState(searchParams.get("mine") === "true");
  // D4 oversight toggles — deep-linkable from the Home operations cards.
  const [unassigned, setUnassigned] = useState(searchParams.get("unassigned") === "true");
  const [overdue, setOverdue] = useState(searchParams.get("overdue") === "true");

  // Bulk assignment (hardwareAdmin+): select many visits → assign to one technician.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [techs, setTechs] = useState([]);
  const [bulkTech, setBulkTech] = useState();
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!isSupervisor) return; // supervisor+ may bulk-assign
    getUsers({ role: ROLES.TECHNICIAN })
      .then((r) => setTechs(r.users || []))
      .catch(() => setTechs([]));
  }, [isSupervisor]);

  const open = (id) => navigate(`/dashboard/visit-detail/${id}`);

  const handleBulkAssign = useCallback(async () => {
    if (!bulkTech || selectedRowKeys.length === 0) return;
    setAssigning(true);
    try {
      const res = await bulkAssignVisits(selectedRowKeys, bulkTech);
      const tech = techs.find((t) => t._id === bulkTech)?.name || "technician";
      const skipped = res.skipped?.length || 0;
      notify.success(
        `Assigned ${res.assigned} visit(s) to ${tech}` + (skipped ? ` · ${skipped} skipped (already finished)` : "")
      );
      setSelectedRowKeys([]);
      setBulkTech(undefined);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      notify.error(serverError(e, "Bulk assignment failed"));
    } finally {
      setAssigning(false);
    }
  }, [bulkTech, selectedRowKeys, techs]);

  const columns = [
    {
      title: <span>Type<InfoHint title="The purpose of the visit — e.g. installation, preventive maintenance, or a repair callout." /></span>,
      dataIndex: "visitType",
      render: (t) => <Tag color={VISIT_TYPE_TAG_COLOR[t]}>{VISIT_TYPE_LABEL[t] || t}</Tag>,
    },
    {
      title: "Outlet",
      key: "outlet",
      render: (_, r) =>
        r.shop ? (
          <span>
            <Text strong>{r.shop.code}</Text>
            {r.shop.title ? <Text type="secondary"> — {r.shop.title}</Text> : null}
          </span>
        ) : (
          r.outletCode || <Text type="secondary">—</Text>
        ),
    },
    { title: "Device", dataIndex: "deviceId", render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Technician",
      key: "technician",
      render: (_, r) => r.technician?.name || <Text type="secondary">Unassigned</Text>,
    },
    {
      title: <span>Status<InfoHint title="Progress of the visit: scheduled → in progress → completed (or cancelled). Completed/cancelled visits can't be reassigned." /></span>,
      dataIndex: "status",
      render: (s) => <Tag color={VISIT_STATUS_TAG_COLOR[s]}>{VISIT_STATUS_LABEL[s] || s}</Tag>,
    },
    {
      title: <span>Scheduled<InfoHint title="When the visit is planned to happen. A past date on a non-completed visit means it's overdue." /></span>,
      dataIndex: "scheduledAt",
      render: (v) => (v ? relativeTime(v) : <Text type="secondary">—</Text>),
    },
    { title: "Updated", dataIndex: "updatedAt", render: (v) => (v ? relativeTime(v) : "—") },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Button type="link" onClick={(e) => { e.stopPropagation(); open(r._id); }}>
          View
        </Button>
      ),
    },
  ];

  // Supervisor+ get checkboxes (they can assign). Finished visits aren't selectable.
  const rowSelection = isSupervisor
    ? {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        getCheckboxProps: (record) => ({ disabled: TERMINAL_STATUSES.includes(record.status) }),
      }
    : undefined;

  const toolbar = (
    <Space wrap>
      <Select allowClear placeholder="Status" style={{ minWidth: 140 }} value={status} onChange={setStatus} options={VISIT_STATUS_OPTIONS} />
      <Select allowClear placeholder="Type" style={{ minWidth: 140 }} value={visitType} onChange={setVisitType} options={VISIT_TYPE_OPTIONS} />
      <Space size={6}>
        <Switch checked={mine} onChange={setMine} size="small" />
        <Text type="secondary">My visits</Text>
      </Space>
      {isHardwareAdmin && (
        <>
          <Space size={6}>
            <Switch checked={unassigned} onChange={setUnassigned} size="small" />
            <Text type="secondary">Unassigned</Text>
          </Space>
          <Space size={6}>
            <Switch checked={overdue} onChange={setOverdue} size="small" />
            <Text type="secondary">Overdue</Text>
          </Space>
        </>
      )}
    </Space>
  );

  return (
    <div>
      <PageHeader
        title="Visits"
        subtitle="Field technician visits & checklists"
        tooltip="Scheduled and completed field visits, each with its checklist and photos. Supervisors can tick several rows to bulk-assign them to a technician. Use the toggles to see your own, unassigned or overdue visits."
        icon={<ScheduleOutlined />}
        extra={
          isSupervisor && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              New Visit
            </Button>
          )
        }
      />

      {/* Bulk-assign action bar — shows once visits are selected. */}
      {isSupervisor && selectedRowKeys.length > 0 && (
        <Flex align="center" gap={12} wrap style={{ marginBottom: 12, padding: "10px 14px", background: "#f0f5ff", border: "1px solid #adc6ff", borderRadius: 8 }}>
          <UsergroupAddOutlined style={{ color: "#2f54eb" }} />
          <Text strong>{selectedRowKeys.length} selected</Text>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 240 }}
            placeholder="Assign to technician…"
            value={bulkTech}
            onChange={setBulkTech}
            options={techs.map((t) => ({ value: t._id, label: t.name }))}
          />
          <Button type="primary" loading={assigning} disabled={!bulkTech} onClick={handleBulkAssign}>
            Assign {selectedRowKeys.length} visit{selectedRowKeys.length > 1 ? "s" : ""}
          </Button>
          <Button type="text" onClick={() => setSelectedRowKeys([])}>Clear</Button>
        </Flex>
      )}

      <DataTable
        rowKey="_id"
        columns={columns}
        fetcher={listVisits}
        refreshKey={refreshKey}
        searchPlaceholder="Search device, outlet or technician…"
        toolbar={toolbar}
        filters={{ status, visitType, mine, unassigned, overdue }}
        rowSelection={rowSelection}
        onRow={(r) => ({ onClick: () => open(r._id) })}
        emptyText="No visits"
      />

      <AddVisit
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
